import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { totalPagesFromCount } from "../../../shared/ui/Pagination/paginationModel";
import { messageFromError } from "../../../shared/api/drfErrors";
import LifecycleConfirmModal from "../../realty/widgets/LifecycleConfirmModal";

import PropertyCard from "./PropertyCard";
import ProfileTypeSortFilters from "./ProfileTypeSortFilters";

import {
  deleteProperty,
  fetchProperties,
  patchPropertyStatus,
  togglePropertyBookmark,
  togglePropertyLike,
} from "../api/realty";

import {
  OWNER_EMPTY_STATES,
  OWNER_STATUS_FILTERS,
  buildOwnerCardMenuItems,
  getOwnerCardModerationNotice,
  lifecycleSuccessMessage,
  normalizeOwnerStatusFilter,
  ownerListParams,
} from "../lib/ownerDashboard";
import {
  PROFILE_DEFAULT_DEAL_TYPE,
  PROFILE_DEFAULT_SORT,
  normalizeProfileDealType,
  normalizeProfileSort,
} from "../lib/profilePropertyTypes";

const DEFAULT_SORT = PROFILE_DEFAULT_SORT;
const DEFAULT_DEAL_TYPE = PROFILE_DEFAULT_DEAL_TYPE;
const PAGE_SIZE = 20;

function readPropertyStatusFilter(searchParams) {
  const raw = searchParams.get("property_status");
  if (!raw) return "all";
  const match = OWNER_STATUS_FILTERS.find((item) => item.statusParam === raw);
  if (match) return match.id;
  return normalizeOwnerStatusFilter(raw);
}

function showBootstrapModal(elementId) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(elementId);
  if (!el || !window.bootstrap?.Modal) return;
  window.bootstrap.Modal.getOrCreateInstance(el).show();
}

function ProfileRealtyObjects() {
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() =>
    readPropertyStatusFilter(searchParams),
  );
  const [dealType, setDealType] = useState(DEFAULT_DEAL_TYPE);
  const [typeSet, setTypeSet] = useState(() => new Set());
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [retryToken, setRetryToken] = useState(0);

  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadState, setLoadState] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [likedIds, setLikedIds] = useState(new Set());
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [likingIds, setLikingIds] = useState(new Set());
  const [bookmarkingIds, setBookmarkingIds] = useState(new Set());
  const likingIdsRef = useRef(new Set());
  const bookmarkingIdsRef = useRef(new Set());
  const [engagementError, setEngagementError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [lifecycleModal, setLifecycleModal] = useState(null);
  const [pendingPropertyId, setPendingPropertyId] = useState(null);
  const lifecycleBusyRef = useRef(false);

  const requestIdRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    setStatusFilter(readPropertyStatusFilter(searchParams));
    setPage(1);
  }, [searchParams]);

  const backendParams = useMemo(
    () =>
      ownerListParams({
        statusFilter: normalizeOwnerStatusFilter(statusFilter),
        typeSet,
        sort: normalizeProfileSort(sort),
        dealType: normalizeProfileDealType(dealType),
        page,
        pageSize: PAGE_SIZE,
      }),
    [statusFilter, typeSet, sort, dealType, page],
  );

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const refreshing = hasLoadedOnceRef.current;
    if (refreshing) {
      setIsRefreshing(true);
    } else {
      setLoadState("loading");
    }
    setLoadError(null);

    fetchProperties(backendParams)
      .then((data) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const rows = Array.isArray(data)
          ? data
          : (data && Array.isArray(data.results) ? data.results : []);
        const count = Array.isArray(data) ? rows.length : (data?.count ?? rows.length);
        setResults(rows);
        setTotalCount(count);
        setLikedIds(new Set(rows.filter((row) => row.liked_by_me).map((row) => row.id)));
        setBookmarkedIds(
          new Set(rows.filter((row) => row.bookmarked_by_me).map((row) => row.id)),
        );
        hasLoadedOnceRef.current = true;
        setLoadState("loaded");
        setIsRefreshing(false);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setLoadError(messageFromError(err));
        setLoadState("error");
        setIsRefreshing(false);
      });
  }, [backendParams, retryToken]);

  const selectStatusFilter = (nextFilter) => {
    setStatusFilter(normalizeOwnerStatusFilter(nextFilter));
    setPage(1);
  };

  const selectDealType = (nextDeal) => {
    setDealType(normalizeProfileDealType(nextDeal));
    setPage(1);
  };

  const toggleType = (value) => {
    const next = new Set(typeSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setTypeSet(next);
    setPage(1);
  };

  const selectSort = (nextSort) => {
    setSort(normalizeProfileSort(nextSort));
    setPage(1);
  };

  const handleToggleLike = async (property) => {
    if (!property || likingIdsRef.current.has(property.id)) return;
    likingIdsRef.current.add(property.id);
    setLikingIds(new Set(likingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyLike(property.id);
      setLikedIds((prev) => {
        const next = new Set(prev);
        if (data && data.liked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      likingIdsRef.current.delete(property.id);
      setLikingIds(new Set(likingIdsRef.current));
    }
  };

  const handleToggleBookmark = async (property) => {
    if (!property || bookmarkingIdsRef.current.has(property.id)) return;
    bookmarkingIdsRef.current.add(property.id);
    setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    setEngagementError(null);
    try {
      const data = await togglePropertyBookmark(property.id);
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (data && data.bookmarked) next.add(property.id);
        else next.delete(property.id);
        return next;
      });
    } catch (err) {
      setEngagementError(messageFromError(err));
    } finally {
      bookmarkingIdsRef.current.delete(property.id);
      setBookmarkingIds(new Set(bookmarkingIdsRef.current));
    }
  };

  const handleShareCopy = async (property) => {
    const url = `${window.location.origin}/realty/${property.id}`;
    try {
      if (
        typeof navigator === "undefined"
        || !navigator.clipboard
        || typeof navigator.clipboard.writeText !== "function"
      ) {
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopiedId(property.id);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === property.id ? null : curr));
      }, 2000);
    } catch (_err) {
      /* silent */
    }
  };

  const openLifecycleModal = (property, action) => {
    setLifecycleModal({ property, action });
    window.setTimeout(() => {
      showBootstrapModal("profile-owner-lifecycle-modal");
    }, 0);
  };

  const closeLifecycleModal = () => {
    setLifecycleModal(null);
  };

  const handleLifecycleConfirm = async () => {
    if (!lifecycleModal || lifecycleBusyRef.current) return;
    const { property, action } = lifecycleModal;
    if (!property || !action) return;

    lifecycleBusyRef.current = true;
    setPendingPropertyId(property.id);

    try {
      if (action.id === "delete") {
        await deleteProperty(property.id);
        toast.success("Оголошення видалено.");
        setResults((prev) => prev.filter((row) => row.id !== property.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
        closeLifecycleModal();
        return;
      }

      const targetStatus = action.targetStatus;
      if (!targetStatus) {
        return;
      }

      const updated = await patchPropertyStatus(property.id, targetStatus);
      toast.success(lifecycleSuccessMessage(property, updated.status || targetStatus));

      const activeFilter = normalizeOwnerStatusFilter(statusFilter);
      const filterDef = OWNER_STATUS_FILTERS.find((item) => item.id === activeFilter);
      const matchesFilter =
        activeFilter === "all"
        || filterDef?.statusParam === (updated.status || targetStatus);

      if (matchesFilter) {
        setResults((prev) =>
          prev.map((row) =>
            row.id === property.id
              ? {
                  ...row,
                  ...updated,
                  status: updated.status || targetStatus,
                  is_moderation_locked:
                    updated.is_moderation_locked ?? row.is_moderation_locked,
                }
              : row,
          ),
        );
      } else {
        setResults((prev) => prev.filter((row) => row.id !== property.id));
        setTotalCount((prev) => Math.max(0, prev - 1));
      }

      closeLifecycleModal();
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      lifecycleBusyRef.current = false;
      setPendingPropertyId(null);
    }
  };

  const emptyState = OWNER_EMPTY_STATES[normalizeOwnerStatusFilter(statusFilter)]
    || OWNER_EMPTY_STATES.all;

  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const canPaginate = totalCount > PAGE_SIZE;

  const statusField = (
    <div className="profile-filters__field">
      <span className="profile-filters__label">Статус</span>
      <div
        className="profile-filters__segmented"
        role="tablist"
        aria-label="Статус оголошень"
      >
        {OWNER_STATUS_FILTERS.map((filter) => {
          const selected = statusFilter === filter.id;
          return (
            <label key={filter.id} className="profile-filters__seg">
              <input
                type="radio"
                name="profile-status"
                value={filter.id}
                checked={selected}
                onChange={() => selectStatusFilter(filter.id)}
              />
              <span>{filter.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div
        className="profile-filters"
        role="group"
        aria-label="Фільтри та сортування"
      >
        <ProfileTypeSortFilters
          extraMainFields={statusField}
          dealType={dealType}
          onDealTypeChange={selectDealType}
          typeSet={typeSet}
          onToggleType={toggleType}
          sort={sort}
          onSortChange={selectSort}
          sortSelectId="profile-sort"
          typeInputName="profile-type"
          dealInputName="profile-deal-type"
        />
      </div>

      {engagementError ? (
        <div className="feature-realty-list__engagement-error" role="alert">
          {engagementError}
        </div>
      ) : null}

      {isRefreshing ? (
        <div className="feature-realty-list__state" aria-live="polite">
          <Spinner size="sm" label="Оновлення списку" />
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-realty-list__state">
          <Spinner size="lg" label="Завантаження оголошень" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-realty-list__state feature-realty-list__state--stacked">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити оголошення"
            description={loadError || "Спробуйте ще раз."}
            size="md"
            primaryAction={
              <button
                type="button"
                className="action-btn"
                onClick={() => setRetryToken((token) => token + 1)}
              >
                Спробувати знову
              </button>
            }
          />
        </div>
      ) : results.length === 0 ? (
        <div className="feature-realty-list__state feature-realty-list__state--stacked">
          <EmptyState
            icon="fa-solid fa-house"
            title={emptyState.title}
            description={emptyState.description}
            size="md"
            primaryAction={
              emptyState.showCreate ? (
                <Link to="/add" className="action-btn">
                  Створити оголошення
                </Link>
              ) : null
            }
          />
        </div>
      ) : (
        <>
          <div className="row">
            {results.map((property) => {
              const moderationNotice = getOwnerCardModerationNotice(property);
              return (
                <div
                  key={property.id}
                  className="card-elem col-12 col-lg-6 col-xxl-4"
                >
                  {moderationNotice ? (
                    <p className="muted" role="status">
                      {moderationNotice}
                    </p>
                  ) : null}
                  <PropertyCard
                    property={property}
                    detailHref={`/realty/${property.id}`}
                    liked={likedIds.has(property.id)}
                    bookmarked={bookmarkedIds.has(property.id)}
                    likePending={likingIds.has(property.id)}
                    bookmarkPending={bookmarkingIds.has(property.id)}
                    onToggleLike={() => handleToggleLike(property)}
                    onToggleBookmark={() => handleToggleBookmark(property)}
                    menuItems={buildOwnerCardMenuItems(property, {
                      onShareCopy: handleShareCopy,
                      copiedId,
                      onLifecycleAction: openLifecycleModal,
                      isActionPending: (id) => pendingPropertyId === id,
                    })}
                    showLocationUpgradeNotice
                  />
                </div>
              );
            })}
          </div>

          {canPaginate ? (
            <NumberedPagination
              currentPage={page}
              totalPages={totalPages}
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPageChange={(nextPage) => setPage(nextPage)}
              ariaLabel="Сторінки оголошень"
              disabled={isRefreshing}
            />
          ) : null}
        </>
      )}

      <LifecycleConfirmModal
        id="profile-owner-lifecycle-modal"
        actionId={lifecycleModal?.action?.id}
        property={lifecycleModal?.property}
        onConfirm={handleLifecycleConfirm}
        isBusy={Boolean(pendingPropertyId)}
        confirmClassName={
          lifecycleModal?.action?.destructive
            ? "action-btn action-btn--danger"
            : lifecycleModal?.action?.successClass
              ? `action-btn ${lifecycleModal.action.successClass}`
              : "action-btn action-btn--success"
        }
      />
    </>
  );
}

export default ProfileRealtyObjects;
